import { useQuery } from '@tanstack/react-query';

import { supabase } from '@/lib/supabase';

/**
 * useKbArticles / useKbArticle — customer-facing reads of the help library
 * (the same orders.kb_documents that power Mr. Bubbles), via the
 * kb-articles function. Content managed on /admin/knowledge shows up here
 * immediately — no deploy.
 */

export interface KbArticleSummary {
  slug: string;
  title: string | null;
  category: string | null;
  updated_at: string | null;
}

export interface KbArticle extends KbArticleSummary {
  content: string;
}

async function authedGet<T>(path: string): Promise<T> {
  const { data } = await supabase.auth.getSession();
  const token = data.session?.access_token;
  if (!token) throw new Error('not signed in');
  const res = await fetch(`/.netlify/functions/${path}`, {
    headers: { Authorization: `Bearer ${token}` },
  });
  const body = (await res.json()) as T & { ok?: boolean; error?: string };
  if (!res.ok || body.ok === false) throw new Error(body.error ?? `HTTP ${res.status}`);
  return body;
}

export function useKbArticles() {
  return useQuery({
    queryKey: ['kb-articles'],
    queryFn: async () => {
      const res = await authedGet<{ articles: KbArticleSummary[] }>('kb-articles');
      return res.articles;
    },
    staleTime: 5 * 60_000,
  });
}

export function useKbArticle(slug: string | undefined) {
  return useQuery({
    queryKey: ['kb-article', slug],
    enabled: !!slug,
    queryFn: async () => {
      const res = await authedGet<{ article: KbArticle }>(
        `kb-articles?slug=${encodeURIComponent(slug!)}`,
      );
      return res.article;
    },
    staleTime: 5 * 60_000,
  });
}
